// Accepted file type enum
export enum AcceptedFileType {
  IMAGE = 'image',
  VIDEO = 'video',
  PDF = 'pdf',
  YOUTUBE = 'youtube',
}

// Tab type enum for FileUploadField
export enum FileUploadTabType {
  FILE_UPLOAD = 'file',
  EXTERNAL_URL = 'url',
  MEDIA_LIBRARY = 'library',
}

// File type categories
export const FILE_TYPE_CATEGORIES = {
  image: {
    label: 'Image',
    mimeTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
  },
  video: {
    label: 'Video',
    mimeTypes: ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'],
    extensions: ['.mp4', '.webm', '.ogg', '.mov', '.avi'],
  },
  pdf: {
    label: 'PDF',
    mimeTypes: ['application/pdf'],
    extensions: ['.pdf'],
  },
  youtube: {
    label: 'YouTube',
    mimeTypes: ['video/youtube'],
    extensions: [],
  },
} as const;

export type FileTypeCategory = keyof typeof FILE_TYPE_CATEGORIES;

// Build extension to MIME type mapping from FILE_TYPE_CATEGORIES
export const EXTENSION_TO_MIME_TYPE: Record<string, string> = {};

Object.entries(FILE_TYPE_CATEGORIES).forEach(([_, category]) => {
  category.extensions.forEach((ext, index) => {
    const extLower = ext.toLowerCase();

    // Special case: .jpg and .jpeg both map to image/jpeg
    if (extLower === '.jpg' || extLower === '.jpeg') {
      EXTENSION_TO_MIME_TYPE[extLower] = 'image/jpeg';
      return;
    }

    // For other extensions, use index-based mapping (arrays are aligned)
    const mimeType = category.mimeTypes[index] || category.mimeTypes[0];
    if (mimeType) {
      EXTENSION_TO_MIME_TYPE[extLower] = mimeType;
    }
  });
});

// Utility function to get MIME type from file extension
export const getMimeTypeFromExtension = (filename: string): string | null => {
  const extension = filename.substring(filename.lastIndexOf('.')).toLowerCase();
  return EXTENSION_TO_MIME_TYPE[extension] || null;
};

// Utility function to check if a type is a file upload type (not YouTube)
export const isFileUploadType = (type: AcceptedFileType): boolean => {
  return type !== AcceptedFileType.YOUTUBE;
};

// Utility function to get accept string for file input from accepted types
export const getAcceptStringForInput = (acceptedTypes: AcceptedFileType[]): string => {
  if (acceptedTypes.length === 0) return '*';

  const mimeTypes: string[] = [];
  acceptedTypes.forEach((type) => {
    if (type in FILE_TYPE_CATEGORIES) {
      const category = FILE_TYPE_CATEGORIES[type as FileTypeCategory];
      mimeTypes.push(...category.mimeTypes);
    }
  });

  return mimeTypes.length > 0 ? mimeTypes.join(',') : '*';
};

// Utility function to get expected MIME type from accepted types
export const getMimeTypeFromAcceptedTypes = (acceptedTypes: AcceptedFileType[]): string | null => {
  if (acceptedTypes.length === 0) return null;

  const firstType = acceptedTypes[0];
  if (firstType in FILE_TYPE_CATEGORIES) {
    const category = FILE_TYPE_CATEGORIES[firstType as FileTypeCategory];
    return category.mimeTypes[0];
  }

  return null;
};

// Utility function to validate YouTube URL
export const validateYouTubeUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check for youtube.com or youtu.be domains
    if (
      hostname === 'www.youtube.com' ||
      hostname === 'youtube.com' ||
      hostname === 'youtu.be' ||
      hostname === 'm.youtube.com'
    ) {
      // Check for video ID in various YouTube URL formats
      // youtube.com/watch?v=VIDEO_ID
      // youtube.com/embed/VIDEO_ID
      // youtu.be/VIDEO_ID
      // youtube.com/v/VIDEO_ID
      if (urlObj.pathname.includes('/watch') && urlObj.searchParams.has('v')) {
        return true;
      }
      if (urlObj.pathname.includes('/embed/') || urlObj.pathname.includes('/v/')) {
        return true;
      }
      if (hostname === 'youtu.be' && urlObj.pathname.length > 1) {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
};

// Utility function to validate URL points to expected file type
export const validateUrlFileType = async (url: string, expectedType: AcceptedFileType | null): Promise<boolean> => {
  try {
    // If YouTube type, validate YouTube URL
    if (expectedType === AcceptedFileType.YOUTUBE) {
      return validateYouTubeUrl(url);
    }

    // For other types, get expected MIME type
    const expectedMimeType =
      expectedType && expectedType in FILE_TYPE_CATEGORIES
        ? FILE_TYPE_CATEGORIES[expectedType as FileTypeCategory].mimeTypes[0]
        : null;

    if (!expectedMimeType) {
      return true; // If no specific type expected, allow it
    }

    // First, check URL extension
    const urlPath = new URL(url).pathname.toLowerCase();

    // If we have a specific MIME type, check extension
    for (const [, cat] of Object.entries(FILE_TYPE_CATEGORIES)) {
      const category = cat as (typeof FILE_TYPE_CATEGORIES)[FileTypeCategory];
      if (
        category.mimeTypes.some((mt: string) => mt === expectedMimeType) ||
        category.extensions.some((ext: string) => urlPath.endsWith(ext))
      ) {
        if (category.extensions.some((ext: string) => urlPath.endsWith(ext))) {
          return true;
        }
      }
    }

    // For PDF, check extension
    if (expectedMimeType === 'application/pdf' && urlPath.endsWith('.pdf')) {
      return true;
    }

    // Try to fetch HEAD request to check Content-Type
    try {
      await fetch(url, { method: 'HEAD', mode: 'no-cors' });
      // Note: In no-cors mode, we can't read headers, so we'll rely on extension check
    } catch {
      // If HEAD fails, we'll rely on extension validation
      return false;
    }

    // If we can't verify via HEAD, check extension match
    for (const [, cat] of Object.entries(FILE_TYPE_CATEGORIES)) {
      const category = cat as (typeof FILE_TYPE_CATEGORIES)[FileTypeCategory];
      if (category.mimeTypes.some((mt: string) => mt === expectedMimeType)) {
        if (category.extensions.some((ext: string) => urlPath.endsWith(ext))) {
          return true;
        }
      }
    }

    // Direct MIME type check for PDF
    if (expectedMimeType === 'application/pdf') {
      return urlPath.endsWith('.pdf');
    }

    return true; // If we can't validate, allow it (user responsibility)
  } catch {
    return false;
  }
};

// Utility function to get file name from URL
export const getFileNameFromUrl = (url: string, type?: AcceptedFileType): string => {
  try {
    // For YouTube, extract video ID
    if (type === AcceptedFileType.YOUTUBE) {
      const urlObj = new URL(url);
      let videoId: string | null = null;

      if (urlObj.searchParams.has('v')) {
        videoId = urlObj.searchParams.get('v');
      } else if (urlObj.hostname === 'youtu.be') {
        videoId = urlObj.pathname.substring(1);
      } else if (urlObj.pathname.includes('/embed/')) {
        videoId = urlObj.pathname.split('/embed/')[1]?.split('/')[0] || null;
      }

      return videoId ? `youtube-${videoId}` : 'youtube-video';
    }

    const urlPath = new URL(url).pathname;
    const fileName = urlPath.split('/').pop() || 'external-file';
    return fileName.includes('.') ? fileName : `${fileName}.file`;
  } catch {
    return 'external-file';
  }
};

// Utility function to get MIME type from file type category
export const getMimeTypeFromCategory = (category: FileTypeCategory): string => {
  return FILE_TYPE_CATEGORIES[category].mimeTypes[0];
};

// Utility function to get provider from accepted type
export const getProviderFromType = (type: AcceptedFileType): 's3' | 'external' | 'youtube' => {
  if (type === AcceptedFileType.YOUTUBE) {
    return 'youtube';
  }
  // For file uploads, it's s3, for external URLs it's external
  // This will be determined by the component using it
  return 'external';
};

// Utility function to convert acceptedTypes to fileType MIME pattern for MediaLibrary
export const convertAcceptedTypesToFileType = (acceptedTypes: AcceptedFileType[]): string | undefined => {
  if (acceptedTypes.length === 0) {
    return undefined; // Show all types
  }

  // If only one type, return its MIME pattern
  if (acceptedTypes.length === 1) {
    const type = acceptedTypes[0];
    if (type === AcceptedFileType.IMAGE) return 'image/*';
    if (type === AcceptedFileType.VIDEO) return 'video/*';
    if (type === AcceptedFileType.PDF) return 'application/pdf';
    // YouTube is not a file type for MediaLibrary (it's external)
    return undefined;
  }

  // If multiple types, we need to combine them
  // MediaLibraryContainer supports single fileType, so we'll need to filter client-side
  // For now, return undefined to show all and let MediaLibrary filter
  // Or we could return the first type's pattern
  const hasImage = acceptedTypes.includes(AcceptedFileType.IMAGE);
  const hasVideo = acceptedTypes.includes(AcceptedFileType.VIDEO);
  const hasPdf = acceptedTypes.includes(AcceptedFileType.PDF);

  // If we have image and video, we can't use a single pattern
  // Return undefined to show all, filtering will be done by acceptedTypes
  if ((hasImage && hasVideo) || (hasImage && hasPdf) || (hasVideo && hasPdf)) {
    return undefined;
  }

  // Single type case (shouldn't reach here, but just in case)
  if (hasImage) return 'image/*';
  if (hasVideo) return 'video/*';
  if (hasPdf) return 'application/pdf';

  return undefined;
};
